import { useColorScheme } from '@mui/material/styles';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  PropsWithChildren,
  useMemo,
  useCallback,
} from 'react';
import {
  ColorMode,
  defaultConfig,
  getStoredConfig,
  ReaderConfiguration,
  STORAGE_KEY,
} from './utils/ConfigUtils';

interface ReaderConfigurationContextType {
  config: ReaderConfiguration;
  updateConfig: (newConfig: Partial<ReaderConfiguration>) => void;
  resetTheme: () => void;
}

const ReaderConfigurationContext =
  createContext<ReaderConfigurationContextType | null>(null);

export function ReaderConfigurationProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const { setMode } = useColorScheme();
  const [config, setConfig] = useState<ReaderConfiguration>(getStoredConfig());

  const updateConfig = useCallback(
    (newConfig: Partial<ReaderConfiguration>) => {
      setConfig((prevConfig) => {
        const updatedConfig = {
          ...prevConfig,
          ...newConfig,
          colorMode: 'light' as ColorMode, // Override color mode to light until dark theme is configured properly
          showHighlights: false, // Override show highlights to false until highlights feature is implemented
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedConfig));
        return updatedConfig;
      });
    },
    [],
  );

  const resetTheme = useCallback(() => {
    setConfig(defaultConfig);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  useEffect(() => {
    setMode(config.colorMode);
  }, [config.colorMode, setMode]);

  const contextValue = useMemo(
    () => ({ config, updateConfig, resetTheme }),
    [config, updateConfig, resetTheme],
  );

  return (
    <ReaderConfigurationContext.Provider value={contextValue}>
      {children}
    </ReaderConfigurationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReaderConfiguration() {
  const context = useContext(ReaderConfigurationContext);
  if (!context) {
    throw new Error(
      'useReaderConfiguration must be used within a ReaderConfigurationProvider',
    );
  }
  return context;
}
